import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Shield,
  ShieldAlert,
  X,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'
import { cn } from '@/lib/utils'

/* ── Types ── */

interface Contract {
  id: string
  showId: string | null
  type: string
  title: string
  counterparty: string
  receivedAt: string
  documentUrl: string | null
  extractedText: string | null
  status: string
  signedAt: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  review?: ContractReview | null
  obligations?: ContractObligation[]
}

interface ContractReview {
  id: string
  contractId: string
  executiveSummary: string
  commercialTerms: unknown
  redFlagCount: number
  amberFlagCount: number
  blueFlagCount: number
  missingClauseCount: number
  overallRisk: 'low' | 'medium' | 'high' | 'critical'
  disclaimer: string
  rawOutput: string | null
  flags?: ContractFlag[]
}

interface ContractFlag {
  id: string
  reviewId: string
  severity: 'red' | 'amber' | 'blue'
  clauseRef: string | null
  quote: string | null
  plainEnglish: string
  standardPractice: string | null
  suggestedAction: string | null
  resolved: boolean
  resolvedNote: string | null
  resolvedBy: string | null
  resolvedAt: string | null
}

interface ContractObligation {
  id: string
  contractId: string
  trigger: string
  triggerDate: string | null
  description: string
  responsibleParty: 'artist' | 'promoter' | 'both'
  addedToCalendar: boolean
  taskCreated: boolean
  completedAt: string | null
}

interface ContractListResponse {
  data: Contract[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

const CONTRACT_TYPES = [
  'performance_agreement',
  'venue_agreement',
  'support_act_agreement',
  'sponsorship_agreement',
  'broadcast_agreement',
  'merchandise_agreement',
  'rider_amendment',
  'settlement_agreement',
  'nda',
  'letter_of_intent',
] as const

const STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  under_review: 'Under Review',
  reviewed: 'Reviewed',
  negotiating: 'Negotiating',
  signed: 'Signed',
  rejected: 'Rejected',
}

const STATUS_COLOURS: Record<string, string> = {
  received: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-purple-100 text-purple-700',
  negotiating: 'bg-amber-100 text-amber-700',
  signed: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
}

const RISK_COLOURS: Record<string, string> = {
  low: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
}

const RISK_BORDER_COLOURS: Record<string, string> = {
  low: 'border-emerald-300',
  medium: 'border-amber-300',
  high: 'border-orange-300',
  critical: 'border-red-300',
}

const FLAG_COLOURS: Record<string, string> = {
  red: 'border-red-200 bg-red-50',
  amber: 'border-amber-200 bg-amber-50',
  blue: 'border-sky-200 bg-sky-50',
}

export function ContractsPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const contracts = useQuery<ContractListResponse, ApiClientError>({
    queryKey: ['contracts', ...scope.queryKeySuffix],
    queryFn: () =>
      artistFetch<ContractListResponse>(`/contracts?limit=100${scope.toQueryString().replace('?', '&')}`),
  })

  const items = scope.filterByShowIds(contracts.data?.data ?? [], (c) => c.showId)

  // Stats
  const totalContracts = items.length
  const underReview = items.filter(
    (c) => c.status === 'under_review' || c.status === 'reviewed',
  ).length
  const redFlagsOutstanding = items.reduce((sum, c) => {
    if (!c.review?.flags) return sum
    return sum + c.review.flags.filter((f) => f.severity === 'red' && !f.resolved).length
  }, 0)
  const upcomingObligations = items.reduce((sum, c) => {
    if (!c.obligations) return sum
    return sum + c.obligations.filter((o) => !o.completedAt && o.triggerDate).length
  }, 0)

  return (
    <div>
      <PageHeader
        title="Contracts"
        subtitle="Manage contracts, AI reviews, and obligations"
      />
      <PerformanceScopeBanner />

      {/* Stats bar */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total contracts" value={totalContracts} icon={<FileText className="h-4 w-4" />} />
        <StatCard label="Under review" value={underReview} icon={<Shield className="h-4 w-4" />} />
        <StatCard label="Red flags outstanding" value={redFlagsOutstanding} icon={<ShieldAlert className="h-4 w-4 text-red-500" />} />
        <StatCard label="Upcoming obligations" value={upcomingObligations} icon={<Calendar className="h-4 w-4" />} />
      </div>

      {/* Actions */}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-4 w-4" />
          Add contract
        </button>
      </div>

      {/* Add contract form */}
      {showForm && (
        <AddContractForm
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false)
            qc.invalidateQueries({ queryKey: ['contracts'] })
          }}
        />
      )}

      {/* Contract list */}
      <AsyncBoundary isLoading={contracts.isLoading} error={contracts.error}>
        <div className="overflow-hidden rounded-lg border border-ovation-ink/10 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ovation-ink/10 bg-ovation-paper/50">
                <th className="px-4 py-3 text-left font-medium text-ovation-muted">Title</th>
                <th className="px-4 py-3 text-left font-medium text-ovation-muted">Counterparty</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ovation-muted md:table-cell">Type</th>
                <th className="px-4 py-3 text-left font-medium text-ovation-muted">Status</th>
                <th className="px-4 py-3 text-left font-medium text-ovation-muted">Risk</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ovation-muted sm:table-cell">Received</th>
                <th className="px-4 py-3 text-left font-medium text-ovation-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-ovation-muted">
                    No contracts yet. Add one to get started.
                  </td>
                </tr>
              )}
              {items.map((contract) => (
                <ContractRow
                  key={contract.id}
                  contract={contract}
                  expanded={expandedId === contract.id}
                  onToggle={() =>
                    setExpandedId(expandedId === contract.id ? null : contract.id)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>
    </div>
  )
}

/* ── Contract Row ── */

function ContractRow({
  contract,
  expanded,
  onToggle,
}: {
  contract: Contract
  expanded: boolean
  onToggle: () => void
}) {
  const risk = contract.review?.overallRisk
  return (
    <>
      <tr
        className="cursor-pointer border-b border-ovation-ink/5 transition-colors hover:bg-ovation-paper/30"
        onClick={onToggle}
      >
        <td className="px-4 py-3 font-medium">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-3 w-3 shrink-0 text-ovation-muted" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0 text-ovation-muted" />
            )}
            {contract.title}
          </div>
        </td>
        <td className="px-4 py-3 text-ovation-muted">{contract.counterparty}</td>
        <td className="hidden px-4 py-3 text-ovation-muted md:table-cell">
          {contract.type.replace(/_/g, ' ')}
        </td>
        <td className="px-4 py-3">
          <span
            className={cn(
              'inline-flex rounded px-2 py-0.5 text-xs uppercase tracking-wider',
              STATUS_COLOURS[contract.status] ?? 'bg-slate-100 text-slate-700',
            )}
          >
            {STATUS_LABELS[contract.status] ?? contract.status}
          </span>
        </td>
        <td className="px-4 py-3">
          {risk ? (
            <span
              className={cn(
                'inline-flex rounded px-2 py-0.5 text-xs uppercase tracking-wider',
                RISK_COLOURS[risk],
              )}
            >
              {risk}
            </span>
          ) : (
            <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wider text-slate-500">
              —
            </span>
          )}
        </td>
        <td className="hidden px-4 py-3 text-ovation-muted sm:table-cell">
          {new Date(contract.receivedAt).toLocaleDateString()}
        </td>
        <td className="px-4 py-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggle()
            }}
            className="text-xs text-ovation-muted underline hover:text-ovation-ink"
          >
            {expanded ? 'Collapse' : 'Details'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="border-b border-ovation-ink/10 bg-ovation-paper/20 px-4 py-4">
            <ContractDetail contract={contract} />
          </td>
        </tr>
      )}
    </>
  )
}

/* ── Contract Detail ── */

function ContractDetail({ contract }: { contract: Contract }) {
  const qc = useQueryClient()
  const [notes, setNotes] = useState(contract.notes ?? '')
  const [savingNotes, setSavingNotes] = useState(false)
  const [requestingReview, setRequestingReview] = useState(false)
  const [disclaimerOpen, setDisclaimerOpen] = useState(false)

  // Fetch full detail with review
  const detail = useQuery<{ data: Contract }, ApiClientError>({
    queryKey: ['contract-detail', contract.id],
    queryFn: () => artistFetch<{ data: Contract }>(`/contracts/${contract.id}`),
  })

  const fullContract = detail.data?.data ?? contract
  const review = fullContract.review
  const obligations = fullContract.obligations ?? []

  async function updateStatus(status: string) {
    await artistFetch(`/contracts/${contract.id}`, {
      method: 'PATCH',
      body: { status },
    })
    qc.invalidateQueries({ queryKey: ['contracts'] })
    qc.invalidateQueries({ queryKey: ['contract-detail', contract.id] })
  }

  async function saveNotes() {
    setSavingNotes(true)
    try {
      await artistFetch(`/contracts/${contract.id}`, {
        method: 'PATCH',
        body: { notes },
      })
      qc.invalidateQueries({ queryKey: ['contracts'] })
    } finally {
      setSavingNotes(false)
    }
  }

  async function requestReview() {
    setRequestingReview(true)
    try {
      await artistFetch(`/contracts/${contract.id}/extract-clauses`, {
        method: 'POST',
      })
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract-detail', contract.id] })
    } finally {
      setRequestingReview(false)
    }
  }

  async function resolveFlag(flagId: string) {
    const note = prompt('Resolution note (optional):')
    if (note === null) return
    await artistFetch(`/contracts/${contract.id}/flags/${flagId}/resolve`, {
      method: 'PATCH',
      body: { resolvedNote: note || undefined },
    })
    qc.invalidateQueries({ queryKey: ['contract-detail', contract.id] })
    qc.invalidateQueries({ queryKey: ['contracts'] })
  }

  return (
    <div className="space-y-6">
      {/* Status bar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-ovation-muted">Status:</span>
        <span
          className={cn(
            'inline-flex rounded px-2 py-0.5 text-xs uppercase tracking-wider',
            STATUS_COLOURS[fullContract.status] ?? 'bg-slate-100 text-slate-700',
          )}
        >
          {STATUS_LABELS[fullContract.status] ?? fullContract.status}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {fullContract.status === 'received' && (
            <StatusButton label="Mark as reviewing" onClick={() => updateStatus('under_review')} />
          )}
          {fullContract.status === 'under_review' && (
            <StatusButton label="Mark as reviewed" onClick={() => updateStatus('reviewed')} />
          )}
          {(fullContract.status === 'reviewed' || fullContract.status === 'negotiating') && (
            <>
              <StatusButton label="Mark as negotiating" onClick={() => updateStatus('negotiating')} />
              <StatusButton label="Mark as signed" onClick={() => updateStatus('signed')} />
              <StatusButton label="Reject" onClick={() => updateStatus('rejected')} className="text-red-600 border-red-200 hover:bg-red-50" />
            </>
          )}
        </div>
      </div>

      {/* Review panel */}
      {review ? (
        <div className={cn('rounded-lg border-2 p-5', RISK_BORDER_COLOURS[review.overallRisk])}>
          <div className="mb-3 flex items-center gap-3">
            <span
              className={cn(
                'inline-flex rounded-md px-3 py-1 text-sm font-semibold uppercase tracking-wider',
                RISK_COLOURS[review.overallRisk],
              )}
            >
              {review.overallRisk} risk
            </span>
            <span className="text-xs text-ovation-muted">
              {review.redFlagCount} red / {review.amberFlagCount} amber / {review.blueFlagCount} blue flags
              {review.missingClauseCount > 0 && ` / ${review.missingClauseCount} missing clauses`}
            </span>
          </div>

          <p className="mb-4 text-sm leading-relaxed">{review.executiveSummary}</p>

          {/* Disclaimer */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setDisclaimerOpen(!disclaimerOpen)}
              className="inline-flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
            >
              {disclaimerOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Disclaimer
            </button>
            {disclaimerOpen && (
              <p className="mt-1 rounded bg-slate-50 p-3 text-xs text-ovation-muted">
                {review.disclaimer}
              </p>
            )}
          </div>

          {/* Flags */}
          {review.flags && review.flags.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Flags
              </h4>
              {['red', 'amber', 'blue'].map((severity) => {
                const flags = review.flags!.filter((f) => f.severity === severity)
                if (flags.length === 0) return null
                return (
                  <div key={severity} className="space-y-2">
                    {flags.map((flag) => (
                      <div
                        key={flag.id}
                        className={cn('rounded-lg border p-4', FLAG_COLOURS[flag.severity])}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            {flag.clauseRef && (
                              <span className="mr-2 inline-flex rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                                {flag.clauseRef}
                              </span>
                            )}
                            {flag.quote && (
                              <p className="mt-1 text-sm italic text-ovation-muted">
                                "{flag.quote}"
                              </p>
                            )}
                            <p className="mt-1 text-sm">{flag.plainEnglish}</p>
                            {flag.standardPractice && (
                              <p className="mt-1 text-xs text-ovation-muted">
                                Standard practice: {flag.standardPractice}
                              </p>
                            )}
                            {flag.suggestedAction && (
                              <p className="mt-1 text-xs font-medium">
                                Suggested: {flag.suggestedAction}
                              </p>
                            )}
                            {flag.resolved && (
                              <p className="mt-2 text-xs text-emerald-700">
                                Resolved{flag.resolvedNote ? `: ${flag.resolvedNote}` : ''}
                              </p>
                            )}
                          </div>
                          {!flag.resolved && (
                            <button
                              type="button"
                              onClick={() => resolveFlag(flag.id)}
                              className="shrink-0 rounded border border-ovation-ink/15 px-2.5 py-1 text-xs font-medium hover:bg-white/80"
                            >
                              Resolve
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )}

          {/* Obligations */}
          {obligations.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Obligations calendar
              </h4>
              <div className="space-y-2">
                {obligations
                  .sort((a, b) => {
                    if (!a.triggerDate) return 1
                    if (!b.triggerDate) return -1
                    return new Date(a.triggerDate).getTime() - new Date(b.triggerDate).getTime()
                  })
                  .map((obl) => (
                    <div
                      key={obl.id}
                      className="flex items-center justify-between rounded border border-ovation-ink/10 bg-white p-3 text-sm"
                    >
                      <div>
                        <div className="font-medium">{obl.description}</div>
                        <div className="text-xs text-ovation-muted">
                          Trigger: {obl.trigger}
                          {obl.triggerDate && ` · ${new Date(obl.triggerDate).toLocaleDateString()}`}
                          {' · '}
                          Responsible: {obl.responsibleParty}
                        </div>
                      </div>
                      {obl.completedAt ? (
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                          Done
                        </span>
                      ) : (
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                          Pending
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-ovation-ink/20 p-6 text-center">
          <p className="mb-3 text-sm text-ovation-muted">
            No AI review has been performed on this contract yet.
          </p>
          <button
            type="button"
            onClick={requestReview}
            disabled={requestingReview}
            className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            <AlertTriangle className="h-4 w-4" />
            {requestingReview ? 'Requesting...' : 'Request AI Review'}
          </button>
        </div>
      )}

      {/* Notes section */}
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-ovation-muted">
          Notes
        </h4>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          placeholder="Add notes about this contract..."
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={saveNotes}
            disabled={savingNotes}
            className="rounded-md border border-ovation-ink/20 px-3 py-1.5 text-xs font-medium hover:bg-ovation-paper disabled:opacity-50"
          >
            {savingNotes ? 'Saving...' : 'Save notes'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── Add Contract Form ── */

function AddContractForm({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [title, setTitle] = useState('')
  const [counterparty, setCounterparty] = useState('')
  const [type, setType] = useState<string>(CONTRACT_TYPES[0])
  const [formNotes, setFormNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!title.trim() || !counterparty.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await artistFetch('/contracts', {
        method: 'POST',
        body: {
          title: title.trim(),
          counterparty: counterparty.trim(),
          type,
          notes: formNotes.trim() || undefined,
          receivedAt: new Date().toISOString(),
        },
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to create contract')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mb-4 rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium">New contract</h3>
        <button type="button" onClick={onClose} className="text-ovation-muted hover:text-ovation-ink">
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="e.g. O2 Arena Performance Agreement"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Counterparty</label>
          <input
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="e.g. Live Nation"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Notes (optional)</label>
          <input
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="Any initial notes..."
          />
        </div>
        <div className="flex items-end gap-2 sm:col-span-2">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {submitting ? 'Creating...' : 'Create contract'}
          </button>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      </form>
    </div>
  )
}

/* ── Small helpers ── */

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number
  icon: React.ReactNode
}) {
  return (
    <div className="rounded-md border border-ovation-ink/10 bg-white p-4">
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-ovation-muted">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  )
}

function StatusButton({
  label,
  onClick,
  className,
}: {
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border border-ovation-ink/15 px-2.5 py-1 text-xs font-medium hover:bg-ovation-ink/5',
        className,
      )}
    >
      {label}
    </button>
  )
}
